import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { layoutsForAllCasesState, DEFAULT_LAYOUT } from './viewerLoaderState';
import type { Layout, Layouts } from './viewerLoaderState';
import { useViewerMetadata } from './useViewerMetadata';
import { useHangingProtocol } from 'domains/viewer/HangingProtocol/useHangingProtocol';
import { EMPTY } from 'config/constants';
import pick from 'ramda/es/pick';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { isPreviewingHangingProtocolState } from '../../HangingProtocol/state';
import { WithApolloContext } from 'modules/Apollo/types';

type UseLayouts = {
  layoutForWindow: Nullable<Layout>;
  layoutsForCase: Layouts;
  setLayoutsForCase: (arg1: Layouts) => void;
  setLayoutForWindow: (arg1: Layout) => void;
  loading: boolean;
};

export const useLayouts = ({ context }: WithApolloContext): UseLayouts => {
  const [allLayouts, setAllLayouts] = useRecoilState(layoutsForAllCasesState);
  const isPreviewingHangingProtocol = useRecoilValue(isPreviewingHangingProtocolState);
  const { openWindowsIds, windowId } = useViewerMetadata();
  const currentCaseId = useCurrentCaseId();
  const { hangingProtocolLayouts, loadingHangingProtocol } = useHangingProtocol({ context });

  const currentLayoutsForCase: Layouts =
    !isPreviewingHangingProtocol && currentCaseId != null
      ? allLayouts[currentCaseId]
      : EMPTY.OBJECT;

  const setLayoutsForCase = useCallback(
    (updatedLayouts: Layouts) => {
      if (currentCaseId == null) return;

      setAllLayouts((currentAllLayouts) => ({
        ...currentAllLayouts,
        [currentCaseId]: updatedLayouts,
      }));
    },
    [currentCaseId, setAllLayouts]
  );

  const setLayoutForWindow = useCallback(
    (layout: Layout) => {
      if (currentCaseId == null || windowId == null) return;

      setAllLayouts((currentAllLayouts) => ({
        ...currentAllLayouts,
        [currentCaseId]: { ...currentAllLayouts[currentCaseId], [windowId]: layout },
      }));
    },
    [currentCaseId, setAllLayouts, windowId]
  );

  // Filter out layouts for any windows which are not currently open.
  // This lets us 'save' the previously used layout and also give an accurate current layout.
  const layoutsForCase: Layouts = useMemo(() => {
    const defaultLayouts: Layouts = Object.fromEntries(
      openWindowsIds.map((windowId) => [windowId, DEFAULT_LAYOUT])
    );

    return pick(openWindowsIds, {
      ...defaultLayouts,
      ...hangingProtocolLayouts,
      ...currentLayoutsForCase,
    });
  }, [currentLayoutsForCase, hangingProtocolLayouts, openWindowsIds]);

  // @ts-expect-error [EN-10263] - TS2538 - Type 'null' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
  const layoutForWindow = layoutsForCase[windowId];

  return {
    layoutForWindow,
    setLayoutForWindow,
    layoutsForCase,
    setLayoutsForCase,
    loading: loadingHangingProtocol,
  };
};
